import { produce } from 'immer';
import { LOCATION_CHANGE, LocationChangeAction } from 'react-router-redux';

import { getSheetKey } from './sheetSelectors';

import { SheetAction, SheetActionTypes } from './sheetActionTypes';
import { SheetFlowId, SheetId, SheetReducerState, SheetState } from './sheetTypes';

const INITIAL_STATE: SheetReducerState = {
    sheets: {},
    flows: {},
};
type SheetOpenAction = Extract<SheetAction, { type: SheetActionTypes.SHEET_OPEN }>;

/**
 * Create the next active sheet state for an open action.
 */
const getNextSheetState = (action: SheetOpenAction): SheetState => {
    const { sheetId, instanceId, config, options } = action;

    return {
        active: true,
        sheetId,
        instanceId,
        activeSnapPoint: 0,
        data: options?.data,
        config,
    };
};

/**
 * Clone a sheet into the shape used when restoring it later.
 */
const getRestorableSheetState = (sheetState: SheetState): SheetState => ({
    ...sheetState,
    active: true,
    activeSnapPoint: 0,
    pendingSheetState: undefined,
});

/**
 * Store a closing sheet while its replacement waits for the close animation to finish.
 */
const getClosingSheetState = (sheetState: SheetState): SheetState => ({
    ...sheetState,
    active: false,
    activeSnapPoint: 0,
});

/**
 * Build a flow stack for the next sheet, popping back when it already exists.
 */
const getNextFlowStack = (
    stack: SheetState[],
    nextSheetState: SheetState,
    pendingSheetState?: SheetState,
): SheetState[] => {
    const nextSheetIndex = stack.findIndex((sheetState) => sheetState.sheetId === nextSheetState.sheetId);
    if (nextSheetIndex >= 0) return [...stack.slice(0, nextSheetIndex), nextSheetState];

    if (pendingSheetState) return [...stack.slice(0, -1), nextSheetState];

    return [...stack, nextSheetState];
};

/**
 * Find the flow stack index to restore when closing the current sheet.
 */
const getRestoreSheetIndex = (stack: SheetState[], popToSheetId?: SheetId): number => {
    if (!popToSheetId) return stack.length - 2;

    const popToSheetIndex = stack.findIndex((stackSheetState) => stackSheetState.sheetId === popToSheetId);
    if (popToSheetIndex >= 0) return popToSheetIndex;

    return stack.length - 2;
};

/**
 * Open a sheet, deferring the handoff when another sheet in the same flow is already present.
 */
const handleFlowSheetOpen = (
    draft: SheetReducerState,
    state: SheetReducerState,
    action: SheetOpenAction,
    sheetKey: string,
    flow: SheetFlowId,
) => {
    const flowState = state.flows[flow];
    const nextSheetState = getNextSheetState(action);
    const currentFlowState = flowState && (flowState.pendingSheetState || flowState.stack[flowState.stack.length - 1]);

    if (!currentFlowState) {
        draft.sheets[sheetKey] = nextSheetState;
        draft.flows[flow] = {
            stack: [nextSheetState],
        };
        return;
    }

    const currentSheetKey = getSheetKey(currentFlowState.sheetId, currentFlowState.instanceId);
    const currentVisibleSheetState = state.sheets[currentSheetKey];
    const nextStack = getNextFlowStack(flowState.stack, nextSheetState, flowState.pendingSheetState);

    if (currentVisibleSheetState) {
        draft.sheets[currentSheetKey] = getClosingSheetState(currentVisibleSheetState);
    }

    draft.flows[flow] = {
        stack: nextStack,
        pendingSheetState: nextSheetState,
    };
};

/**
 * Open a sheet, deferring the handoff when another sheet in the same flow is already present.
 */
const handleSheetOpen = (draft: SheetReducerState, state: SheetReducerState, action: SheetOpenAction) => {
    const { sheetId, instanceId, config } = action;

    const sheetKey = getSheetKey(sheetId, instanceId);
    if (state.sheets[sheetKey]?.active === true) return;

    if (config?.flow) {
        handleFlowSheetOpen(draft, state, action, sheetKey, config.flow);
        return;
    }

    draft.sheets[sheetKey] = getNextSheetState(action);
};

/**
 * Close a flow sheet and optionally restore another sheet in the same flow.
 */
const handleFlowSheetClose = (
    draft: SheetReducerState,
    state: SheetReducerState,
    sheetKey: string,
    sheetState: SheetState,
    action: Extract<SheetAction, { type: SheetActionTypes.SHEET_CLOSE }>,
) => {
    const flow = sheetState.config?.flow;
    if (!flow) return;

    const flowState = state.flows[flow];
    if (!flowState) {
        delete draft.sheets[sheetKey];
        return;
    }

    const restoreSheetIndex = getRestoreSheetIndex(flowState.stack, action.options?.popToSheetId);
    const restoreSheetState =
        restoreSheetIndex >= 0 ? getRestorableSheetState(flowState.stack[restoreSheetIndex]) : undefined;

    if (!restoreSheetState) {
        delete draft.sheets[sheetKey];
        delete draft.flows[flow];
        return;
    }

    draft.sheets[sheetKey] = getClosingSheetState(sheetState);
    draft.flows[flow] = {
        stack: flowState.stack.slice(0, restoreSheetIndex + 1),
        pendingSheetState: restoreSheetState,
    };
};

/**
 * Close a sheet, deferring any restore until the close transition completes.
 */
const handleSheetClose = (
    draft: SheetReducerState,
    state: SheetReducerState,
    action: Extract<SheetAction, { type: SheetActionTypes.SHEET_CLOSE }>,
) => {
    const { sheetId, instanceId } = action;

    const sheetKey = getSheetKey(sheetId, instanceId);
    const sheetState = state.sheets[sheetKey];
    if (!sheetState) return;

    if (sheetState.config?.flow) {
        handleFlowSheetClose(draft, state, sheetKey, sheetState, action);
        return;
    }

    delete draft.sheets[sheetKey];
};

/**
 * Finalise a deferred sheet handoff once the closing sheet has fully unmounted.
 */
const handleSheetCloseTransitionComplete = (
    draft: SheetReducerState,
    state: SheetReducerState,
    action: Extract<SheetAction, { type: SheetActionTypes.SHEET_CLOSE_TRANSITION_DID_COMPLETE }>,
) => {
    const { sheetKey } = action;
    const sheetState = state.sheets[sheetKey];
    if (!sheetState) return;

    const flow = sheetState.config?.flow;
    const pendingSheetState = flow ? state.flows[flow]?.pendingSheetState : sheetState.pendingSheetState;

    // Remove the closing sheet first so the next flow sheet appears only after the
    // previous one has fully unmounted.
    delete draft.sheets[sheetKey];

    if (!pendingSheetState) return;

    const pendingSheetKey = getSheetKey(pendingSheetState.sheetId, pendingSheetState.instanceId);
    draft.sheets[pendingSheetKey] = pendingSheetState;

    if (!flow || !draft.flows[flow]) return;

    draft.flows[flow].pendingSheetState = undefined;
};

export default (state = INITIAL_STATE, action: SheetAction | LocationChangeAction) => {
    switch (action.type) {
        case SheetActionTypes.SHEET_OPEN:
            return produce(state, (draft) => handleSheetOpen(draft, state, action));
        case SheetActionTypes.SHEET_CLOSE:
            return produce(state, (draft) => handleSheetClose(draft, state, action));
        case SheetActionTypes.SHEET_CLOSE_TRANSITION_DID_COMPLETE:
            return produce(state, (draft) => handleSheetCloseTransitionComplete(draft, state, action));
        case SheetActionTypes.SHEET_UPDATE_ACTIVE_SNAP_POINT:
            return produce(state, (draft) => {
                const { sheetId, instanceId } = action;

                const sheetKey = getSheetKey(sheetId, instanceId);
                const sheet = draft.sheets[sheetKey];
                if (!sheet) return;

                sheet.activeSnapPoint = action.activeSnapPoint;

                const flow = sheet.config?.flow;
                if (!flow) return;

                const flowSheetState = draft.flows[flow]?.stack.find(
                    (stackSheetState) => getSheetKey(stackSheetState.sheetId, stackSheetState.instanceId) === sheetKey,
                );
                if (!flowSheetState) return;

                flowSheetState.activeSnapPoint = action.activeSnapPoint;
            });
        case LOCATION_CHANGE:
            return produce(state, (draft) => {
                Object.keys(draft.sheets).forEach((sheetKey) => {
                    const sheet = draft.sheets[sheetKey];
                    if (!sheet?.config?.stayActiveOnLocationChange) {
                        delete draft.sheets[sheetKey];

                        const flow = sheet?.config?.flow;
                        if (flow) delete draft.flows[flow];
                    }
                });
            });
        default:
            return state;
    }
};
